import React, { useCallback, useMemo, useState } from 'react';
import { Box, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';

import PropTypes from 'prop-types';
import CampaignCard from './common/CampaignCard';
import StatusCards from './common/StatusCards';
import StatusTable from './common/StatusTable';
import BackButton from '../common/BackButton';
import QueueStatus from '../paymentqueues/QueueStatus';

// Order here is the tab order. `key` doubles as the StatusTable statusType.
const STATUS_TABS = [
    { code: 'P', key: 'Pending', label: 'Pending' },
    { code: 'A', key: 'Applied', label: 'Applied' },
    { code: 'S', key: 'Success', label: 'Success' },
    { code: 'W', key: 'Waiting', label: 'Waiting' },
    { code: 'R', key: 'Rejected', label: 'Rejected' },
    { code: 'F', key: 'Failed', label: 'Failed' },
];

// Stable fallback so a missing prop doesn't produce a new [] every render
// and bust the activeTabs memo.
const EMPTY_ARRAY = [];

const tabId = (key) => `campaign-status-tab-${key}`;
const panelId = (key) => `campaign-status-tabpanel-${key}`;

function Details({
    campaignDetails,
    handleLinkGenerate,
    campaignInfo,
    isDisabled = false,
    paymentQueue = EMPTY_ARRAY,
    isSuperUser = false,
    onAddRevenue,
    onEditBudgetLimit,
    onFreezeToggle,
    onShare,
}) {
    // Selection is tracked by tab key, not index, so tabs appearing or
    // disappearing (e.g. Payment Queue being prepended after a queue is
    // created) don't silently move the user to a different tab.
    const [selectedKey, setSelectedKey] = useState(null);
    const handleChange = useCallback((event, newKey) => {
        setSelectedKey(newKey);
    }, []);

    // Single memo over the raw props — deriving per-status arrays with
    // `|| []` outside of it created new references every render and made
    // the downstream memos (and memoized StatusTable) useless.
    const activeTabs = useMemo(() => {
        const statusMap = {};
        (campaignInfo || []).forEach((item) => {
            statusMap[item._id] = item.details || [];
        });

        const tabs = STATUS_TABS
            .filter((tab) => statusMap[tab.code]?.length > 0)
            .map((tab) => ({ key: tab.key, label: tab.label, data: statusMap[tab.code] }));

        if (isDisabled && statusMap.isDisable?.length > 0) {
            tabs.push({ key: 'ClubLinks', label: 'Club Links', data: statusMap.isDisable });
        }

        if (paymentQueue.length > 0) {
            tabs.unshift({ key: 'paymentQueue', label: 'Payment Queue', data: paymentQueue });
        }

        return tabs;
    }, [campaignInfo, isDisabled, paymentQueue]);

    // Fall back to the first tab if nothing is selected yet or the selected
    // tab no longer has data — without overwriting the stored selection.
    const activeTab = activeTabs.find((tab) => tab.key === selectedKey) || activeTabs[0];

    return (
        <Box
            width={{ xs: '100%', lg: `calc(100% - 80px)` }}
            marginLeft={{ xs: '0px', lg: `80px` }}
            marginTop={8}
            padding={2}
            backgroundColor={'#F0F2F5'}
            minHeight={'100vh'}
            height={'100%'}
        >
            <Stack spacing={3}>
                <BackButton label={campaignDetails.name} />
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <CampaignCard
                                campaignDetails={campaignDetails}
                                handleLinkGenerate={handleLinkGenerate}
                                isDetailPage={true}
                                isSuperUser={isSuperUser}
                                onAddRevenue={onAddRevenue}
                                onEditBudgetLimit={onEditBudgetLimit}
                                onFreezeToggle={onFreezeToggle}
                                onShare={onShare}
                            />
                        </Grid>

                        <StatusCards campaignInfo={campaignInfo} />
                    </Grid>
                </Box>

                {activeTab ? (
                    <Box>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={activeTab.key}
                                onChange={handleChange}
                                aria-label="status tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                {activeTabs.map((tab) => (
                                    <Tab
                                        key={tab.key}
                                        value={tab.key}
                                        label={`${tab.label} (${tab.data.length})`}
                                        id={tabId(tab.key)}
                                        aria-controls={panelId(tab.key)}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                        {/* Only the active panel is mounted — the others never rendered
                            content anyway, so there's no need for hidden placeholders. */}
                        <Box
                            role="tabpanel"
                            id={panelId(activeTab.key)}
                            aria-labelledby={tabId(activeTab.key)}
                            sx={{ py: 1 }}
                        >
                            {activeTab.key === 'paymentQueue' ? (
                                <QueueStatus queue={activeTab.data} isFrozen={campaignDetails.isFrozen} />
                            ) : (
                                <StatusTable
                                    key={activeTab.key}
                                    campaignInfo={activeTab.data}
                                    statusType={activeTab.key}
                                />
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Box textAlign="center" py={5}>
                        <Typography variant="h6" color="textSecondary">
                            No Payments
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}

Details.propTypes = {
    campaignDetails: PropTypes.shape({
        name: PropTypes.string,
        isFrozen: PropTypes.bool,
    }).isRequired,
    handleLinkGenerate: PropTypes.func,
    campaignInfo: PropTypes.arrayOf(
        PropTypes.shape({
            _id: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
            details: PropTypes.array,
        })
    ),
    isDisabled: PropTypes.bool,
    paymentQueue: PropTypes.array,
    isSuperUser: PropTypes.bool,
    onAddRevenue: PropTypes.func,
    onEditBudgetLimit: PropTypes.func,
    onFreezeToggle: PropTypes.func,
    onShare: PropTypes.func,
};

// The parent now passes only stable callbacks, so memoizing skips
// re-rendering the whole page when unrelated parent state (dialogs,
// error snackbar) changes.
export default React.memo(Details);
